import React, { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FAQRequest } from "services/api-workers-service"
import styled from "styled-components"
import { Box } from "ui/components/atoms/_atoms"
import Button from "ui/components/atoms/button"
import Input from "ui/components/atoms/input"
import { P } from "ui/components/atoms/typography"
import Modal from "ui/components/molecules/modal"

type AddFAQModalProps = {
  show: boolean
  setShow: (show: boolean) => void
  onSubmit: (faq: FAQRequest) => void
  initialData?: FAQRequest | null
  loading?: boolean
}

const AddFAQModal = ({ show, setShow, onSubmit, initialData, loading }: AddFAQModalProps) => {
  const { t } = useTranslation(["panel", "common", "form"])
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      question: initialData?.question || "",
      answer: initialData?.answer || "",
    },
  })

  const question = watch("question")
  const answer = watch("answer")

  useEffect(() => {
    if (show) {
      reset({
        question: initialData?.question || "",
        answer: initialData?.answer || "",
      })
    }
  }, [show, initialData, reset])

  const handleFormSubmit = (data: FAQRequest) => {
    if (data.question.trim().length >= 10 && data.answer.trim().length >= 20) {
      onSubmit(data)
      reset()
    }
  }

  const handleClose = () => {
    reset()
    setShow(false)
  }

  const isValid = question && answer && question.trim().length >= 10 && answer.trim().length >= 20

  return (
    <Modal
      icon="plus"
      show={show}
      setShow={handleClose}
      type="center"
      title={
        initialData ? t("profile.knowledge-base.faqs.edit") : t("profile.knowledge-base.faqs.add")
      }
      text={
        initialData
          ? t("profile.knowledge-base.faqs.edit-text")
          : t("profile.knowledge-base.faqs.add-text")
      }
      $maxWidth="600px"
    >
      <Wrapper>
        <Box $marginBottom="S">
          <Input
            name="question"
            label={`${t("profile.knowledge-base.faqs.question")}*`}
            error={errors.question}
            rhf={register("question", {
              required: t("required", { ns: "form" }),
              minLength: {
                value: 10,
                message: t("profile.knowledge-base.faqs.min-question-length"),
              },
            })}
            multiline
            rows={3}
            $marginBottom="XS"
          />
          {question && question.trim().length > 0 && question.trim().length < 10 && (
            <P $small $color="error" $marginBottom="S">
              {t("profile.knowledge-base.faqs.min-question-length")}
            </P>
          )}
        </Box>
        <Box $marginBottom="S">
          <Input
            name="answer"
            label={`${t("profile.knowledge-base.faqs.answer")}*`}
            error={errors.answer}
            rhf={register("answer", {
              required: t("required", { ns: "form" }),
              minLength: {
                value: 20,
                message: t("profile.knowledge-base.faqs.min-answer-length"),
              },
            })}
            multiline
            rows={5}
            $marginBottom="XS"
          />
          {answer && answer.trim().length > 0 && answer.trim().length < 20 && (
            <P $small $color="error" $marginBottom="S">
              {t("profile.knowledge-base.faqs.min-answer-length")}
            </P>
          )}
        </Box>
        <Button
          $width="100%"
          icon="save"
          onClick={handleSubmit(handleFormSubmit)}
          loading={loading}
          disabled={!isValid}
        >
          {t("save", { ns: "common" })}
        </Button>
      </Wrapper>
    </Modal>
  )
}

export default AddFAQModal

const Wrapper = styled(Box)`
  display: flex;
  flex-direction: column;
`
